/**
 * SOLID Review
 * S — Strong. One concern: an Apollo adapter's operation as a port-safe answer — the server's error, a missing field and a
 *     transport failure each become the same refusal, in the adapter's words; a subscription's error goes to the logger.
 * O — Good. A new operation is one call here from its adapter; a new kind of answer is one method.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Four operations, each the shape one family of adapter methods has.
 * D — Strong. The GraphQL port and the logger it is handed, the shared Result and the Application's refusal code.
 */
import { ok, fail, type Result, type IEntityFailure, type IEntityPortError } from '../../shared/Result';
import { SERVER_REFUSED } from '../../application/ports/contracts';
import type { IPortGraphQLDomainClient, IEntityGraphQLAnswer, IConfigHandlerGraphQLStream, TGraphQLRequest, TGraphQLSubscription } from '../contract/ports';
import type { IServiceLogger } from '../platform/IServiceLogger';

/** The field an answer carries, read from its data. */
type TField<TData, T> = (data: TData) => T | null | undefined;

/**
 * ONE ADAPTER'S CALLS TO THE SERVER (frontend-infrastructure-oo P3, 2026-10-29, §28.288) — the try, the error check, the
 * missing-data check and the catch every Apollo adapter method wrote for itself (about 45 copies), written once.
 *
 * Every failure is the port's: `SERVER_REFUSED`, with the words the adapters used — `<Adapter>.<op> returned a GraphQL error: …`,
 * `… returned no data`, `… failed: …` — because the lobby shows them and the Application branches on the code. Until P3 the
 * adapters failed with an `Error` and `wireResult.fromAdapter` gave every one that code; now the adapter answers it.
 */
export class GraphQLCall {
  constructor(
    private readonly adapter: string,
    private readonly client: IPortGraphQLDomainClient,
    private readonly logger: IServiceLogger,
  ) {}

  /** A query or mutation whose field must be there: an answer without it is refused too. */
  async required<TData, T>(op: string, doing: string, operation: TGraphQLRequest<TData>, field: TField<TData, T>): Promise<Result<T>> {
    return this.run(op, doing, operation, (data) => {
      const value = this.read(data, field);
      return value ? ok(value) : this.refused(`${this.adapter}.${op} returned no data`);
    });
  }

  /** A query whose field may be absent: the server's way of saying there is none. */
  async optional<TData, T>(op: string, doing: string, operation: TGraphQLRequest<TData>, field: TField<TData, T>): Promise<Result<T | null>> {
    return this.run(op, doing, operation, (data) => ok(this.read(data, field) ?? null));
  }

  /** A mutation whose answer nobody reads. */
  async done<TData>(op: string, doing: string, operation: TGraphQLRequest<TData>): Promise<Result<void>> {
    return this.run(op, doing, operation, () => ok(undefined));
  }

  /** A subscription: each answer that carries the field is an event; its error is logged, never thrown. */
  subscribe<TData, T>(event: string, operation: TGraphQLSubscription<TData>, field: TField<TData, T>, onEvent: (value: T) => void): () => void {
    const handlers: IConfigHandlerGraphQLStream<TData> = {
      next: ({ data }) => {
        const value = this.read(data, field);
        if (value) onEvent(value);
      },
      error: (err: unknown) => this.logger.warn(`[${this.adapter}] ${event} subscription error:`, err),
    };
    return this.client.subscribe(operation, handlers);
  }

  private async run<TData, T>(
    op: string, doing: string, operation: TGraphQLRequest<TData>, answer: (data: TData | null | undefined) => Result<T>,
  ): Promise<Result<T>> {
    try {
      const { data, error } = await this.client.request(operation);
      if (error) return this.refused(`${this.adapter}.${op} returned a GraphQL error: ${error.message}`);
      return answer(data);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : `Unknown error ${doing}`;
      return this.refused(`${this.adapter}.${op} failed: ${message}`);
    }
  }

  private read<TData, T>(data: IEntityGraphQLAnswer<TData>['data'], field: TField<TData, T>): T | null | undefined {
    return data === undefined || data === null ? undefined : field(data);
  }

  private refused(message: string): IEntityFailure {
    const refusal: IEntityPortError = { code: SERVER_REFUSED, message };
    return fail(refusal);
  }
}
